<template>
  <div class="video-container--content" :class="{ 'video-container--videoresume': isVideoresume }">
    <div
      v-if="videointerview && videointerview.id"
      :class="[
        'videoInterview__questions',
        has_video_question ? 'videoInterview__questions__video' : '',
        recorderState === 'recording' ? 'state-recording' : ''
      ]"
    >
      <question-paginator
        class="paginator paginator--desktop"
        :list="questions_paginator_list"
        :disabled="isPaginatorDisabled"
        @change_index="change_index"
      />

      <div class="videoInterview__container">
        <div class="videoInterview__questionCounter videoInterview__questionCounter--desktop">
          {{ $tr('videoInterview.questionCounter.question') }}

          <span class="videoInterview__questionCounter_weight">
            {{ questionIndex + 1 }}
            {{ $tr('videoInterview.questionCounter.from') }}
            {{ questions.length }}

            <span v-if="!isCurrentQuestionMandatory || !isRerecordAllowed">&nbsp;|&nbsp;</span>
          </span>

          <span v-if="!isCurrentQuestionMandatory" class="not-mandatory">
            {{ $tr('videoInterview.notMandatory') }}&nbsp;
          </span>

          <span v-if="!isRerecordAllowed">
            {{ $tr('interview.videointerview.cannotRerecord') }}
          </span>

          <div v-if="has_video_question">
            <span class="video-descr">
              <span v-if="has_preparation_time">
                {{ $tr('videoInterview.videoQuestionDescr') }}
              </span>

              <span v-else>
                {{ $tr('videoInterview.videoQuestionDescrWithoutPreparationTime') }}
              </span>
            </span>
          </div>

          <div class="videoInterview__preparation-video">
            <span v-if="preparationTime && !isRerecordAllowed" class="videoInterview__preparation">
              <span>{{ $tr('interview.videointerview.timeToRecord') }}:&nbsp;</span>

              <span
                class="videoInterview__preparation_counter"
                :style="{ color: preparationTime ? '#00d26e' : '#9faab6' }"
              >
                {{ formattedPreparationTime }}
              </span>
            </span>
          </div>
        </div>

        <div class="videoInterview__questions-container">
          <question-paginator-carousel
            class="paginator paginator--mobile"
            :list="questions_paginator_list"
            :disabled="isPaginatorDisabled"
            @change_index="change_index"
          />

          <div class="videoInterview__questionCounter videoInterview__questionCounter--mobile">
            <div class="question-info-left">
              {{ $tr('videoInterview.questionCounter.question') }}&nbsp;

              <span class="videoInterview__questionCounter_weight">
                {{ questionIndex + 1 }}
                {{ $tr('videoInterview.questionCounter.from') }}
                {{ questions.length }}

                <span v-if="!isCurrentQuestionMandatory || !isRerecordAllowed"> &nbsp;|&nbsp;&nbsp; </span>
              </span>
            </div>

            <div class="question-info-right">
              <span v-if="!isCurrentQuestionMandatory" class="not-mandatory">
                {{ $tr('videoInterview.notMandatory') }}&nbsp;
              </span>

              <span v-if="!isRerecordAllowed">
                {{ $tr('interview.videointerview.cannotRerecord') }}
              </span>

              <div v-if="has_video_question">
                <span class="video-descr">
                  <span v-if="has_preparation_time">
                    {{ $tr('videoInterview.videoQuestionDescr') }}
                  </span>

                  <span v-else>
                    {{ $tr('videoInterview.videoQuestionDescrWithoutPreparationTime') }}
                  </span>
                </span>
              </div>

              <div class="videoInterview__preparation-video">
                <span v-if="preparationTime && !isRerecordAllowed" class="videoInterview__preparation">
                  <span>{{ $tr('interview.videointerview.timeToRecord') }}:&nbsp;</span>
                  <span
                    class="videoInterview__preparation_counter"
                    :style="{ color: preparationTime ? '#00d26e' : '#9faab6' }"
                  >
                    {{ formattedPreparationTime }}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div class="video-question-wrap">
            <div
              v-if="!has_video_question && isQuestionHidden && !isRerecordAllowed && currentQuestion.preparation_time"
              class="question-info-block"
            >
              <img src="/static/images/elements/question-timer.svg" />
              {{ $tr('interview.videointerview.timeLimit') }}
            </div>

            <span
              v-if="preparationTime && !isRerecordAllowed"
              class="videoInterview__preparation videoInterview__preparation-text"
            >
              <span>{{ $tr('interview.videointerview.timeToRecord') }}:&nbsp;</span>
              <span
                class="videoInterview__preparation_counter"
                :style="{ color: preparationTime ? '#00d26e' : '#9faab6' }"
              >
                {{ formattedPreparationTime }}
              </span>
            </span>

            <vrecorder
              v-if="has_video_question && is_vrecorder_visible && question_video"
              ref="video_question"
              :class="{
                question_video: has_video_question,
                'is-mobile': isMobile.any
              }"
              :src="question_video"
              :disabled="
                !!is_video_question_blocked ||
                stage === 'recording' ||
                (has_preparation_time && (player_disabled || recorderAction === 'start')) ||
                (has_preparation_time && currentQuestion.preparation_time === 0)
              "
              :show_recorder="false"
              :hide_safari="false"
              :is_youtube="is_youtube"
              :not_editable="has_preparation_time"
              :question-duration="questionDuration"
              :show-question-duration="isShowQuestionDuration"
              :is-mirror="!is_youtube"
              show_player
              is-video-question
              hide-mobile-btns
              @player_play="player_play"
              @player_pause="player_pause"
              @player_end="player_end"
              @onPlayerTimeupdate="onPlayerTimeupdate"
            />
            <!-- :height="296" -->

            <div
              v-if="
                !has_video_question && !(isQuestionHidden && !isRerecordAllowed && currentQuestion.preparation_time)
              "
            >
              <div
                v-if="currentQuestion && currentQuestion.body"
                class="videoInterview__question"
                v-html="currentQuestion.body"
              ></div>
            </div>
          </div>
        </div>

        <div
          class="recorder-section"
          :class="{
            'recorder-section-record': recorderState === 'recording',
            'recorder-only-audio': !isVideoAnswerEnabled
          }"
        >
          <template v-if="noAnswerTime && has_video_question">
            <div class="video-empty-block"></div>

            <vbutton
              v-if="!is_skip_visible"
              :label="'videoInterview.checkVideo.nextQuestion'"
              :loading="loadingRequest"
              :disabled="loadingRequest"
              @click="skipQuestion"
            />
          </template>

          <template v-if="!(noAnswerTime && has_video_question)">
            <vrecorder
              v-if="isStreamRecord ? !(is_show_player && !video_player_src) && isRecorderLoaded : true"
              ref="recorder"
              :class="{ 'is-mobile': isMobile.any }"
              :src="video_player_src"
              :duration="duration"
              :action="recorderAction"
              :disabled="!(has_video_question ? true : currentQuestion ? currentQuestion.is_shown : true)"
              :disabled-recording-button="
                has_video_question && has_preparation_time && !is_player_ended && !is_video_question_blocked
              "
              :upload-progress="uploadProgress"
              :show_recorder="show_video_recorder"
              :show_player="is_show_player"
              :is_retake_video="isRerecordAllowed && !recording"
              :is_big="!!has_video_question"
              :width="444"
              :is-full-screen="recorderState === 'recording'"
              :is-ios="isStreamRecord"
              :is-videoresume="isVideoresume"
              :has-video="isVideoAnswerEnabled"
              hide-mobile-btns
              is_playsinline
              @player_play="recorder_play"
              @setState="setRecorderState"
              @startRecording="startRecording"
              @stopRecording="stopRecording"
              @completeRecording="completeRecording"
              @retakeTestVideo="retakeTestVideo"
              @player_error="player_error"
            />
            <!-- :height="296" -->
            <!-- :is-stream="!!isStreamRecord" -->
            <stream-video-player
              v-if="isStreamRecord && is_show_player && !video_player_src"
              ref="stream_player"
              :interview="interview"
              :src="video_player_src"
              :is-stopped="isStopped"
              :is-playing="isStreamPlaying"
              :is-stream-playing-end="isStreamPlayingEnd"
              is-fetch
              @startStream="startPlayout"
              @startPlayer="startPlayer"
            />
          </template>

          <vue-draggable-resizable
            v-if="!has_video_question"
            class="foo"
            :class="{ 'foo-video': recorderState === 'recording' }"
            :y="y"
            :min-height="88"
            :w="100"
            :h="100"
            :handles="['tm']"
            :draggable="false"
            :parent="false"
            @resizing="onResize"
          >
            <div
              v-if="
                !has_video_question && !(isQuestionHidden && !isRerecordAllowed && currentQuestion.preparation_time)
              "
              class="foo-content-wrapper"
            >
              <div
                v-if="currentQuestion && currentQuestion.body"
                class="foo-content"
                v-html="currentQuestion.body"
              ></div>
            </div>
          </vue-draggable-resizable>

          <div class="footer-section-wrapper">
            <div v-if="is_skip_visible" class="skip-block">
              <div
                v-if="currentQuestion && !currentQuestion.is_required && !isSkipped"
                class="vcv-button button_custom"
                :disabled="is_skip_disable || loadingRequest"
                @click="skipQuestion"
              >
                <div
                  v-if="preparationTimer"
                  v-tooltip="{
                    content: `${tooltip_content}`,
                    placement: 'bottom-center'
                  }"
                  class="skip-block__tooltip"
                ></div>
                {{ $tr('videoInterview.checkVideo.skip') }}
              </div>
            </div>

            <vbutton
              v-if="isRerecordAllowed && !recording && is_show_player && switchToPlayer"
              class="vid-init-videoTest__rerecord-button"
              :text="$tr('videoInterview.checkVideo.retakeTest')"
              :disabled="loadingRequest"
              color="grey"
              bg_color="white"
              @click="retakeTestVideo(true)"
            />

            <vbutton
              v-if="!has_video_question && isQuestionHidden && !isRerecordAllowed && currentQuestion.preparation_time"
              :loading="loadingRequest"
              :disabled="loadingRequest"
              label="videoInterview.checkVideo.show"
              @click="showQuestion"
            />

            <div
              v-if="
                is_available(questionIndex + 1) &&
                recorderState !== 'recording' &&
                (recorderState !== 'uploading' || (currentQuestion && !!currentQuestion.video))
              "
              class="next-question"
            >
              <vbutton
                :label="
                  currentQuestion && !!currentQuestion.video
                    ? 'videoInterview.checkVideo.nextQuestionAndContinue'
                    : 'videoInterview.checkVideo.skip'
                "
                :loading="loadingRequest"
                :disabled="loadingRequest"
                @click="change_index(questionIndex + 1)"
              />
            </div>

            <div
              v-if="isSubmitEnabled()"
              class="pagination__button-container pagination__button-container--desktop mobile-hidden"
            >
              <vbutton
                label="videoInterview.checkVideo.nextQuestionAndFinish"
                :loading="loadingRequest"
                :disabled="loadingRequest"
                @click="saveVideointerview"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="fixed-block-interview"
      :class="{
        'fixed-block-interview--recording': recorderState === 'recording'
      }"
    >
      <transition name="fade">
        <div
          v-if="is_skip_visible && recorderState !== 'recording' && recorderState !== 'uploading' && !isSkipped"
          class="skip-block"
        >
          <div
            v-if="currentQuestion && !currentQuestion.is_required"
            class="vcv-button button_custom"
            :disabled="is_skip_disable || loadingSkipQuestion || loadingRequest"
            @click="skipQuestion"
          >
            <div
              v-if="preparationTimer"
              v-tooltip="{
                content: `${tooltip_content}`,
                placement: 'bottom-center'
              }"
              class="skip-block__tooltip"
            ></div>

            <span v-if="is_skip_disable || loadingSkipQuestion || loadingRequest" class="loader">
              <span class="loading-spinner"></span>
            </span>

            <span v-else>{{ $tr('videoInterview.checkVideo.skip') }}</span>
          </div>
        </div>
      </transition>

      <transition name="fade">
        <vbutton
          v-if="isRerecordAllowed && !recording && is_show_player && switchToPlayer"
          class="button-grey"
          label="videoInterview.checkVideo.retakeTest"
          :loading="loadingRequest"
          :disabled="loadingRequest"
          color="grey"
          bg_color="white"
          @click="retakeTestVideo(true)"
        />
      </transition>

      <transition name="fade">
        <vbutton
          v-if="!has_video_question && isQuestionHidden && !isRerecordAllowed && currentQuestion.preparation_time"
          label="videoInterview.checkVideo.show"
          :loading="loadingRequest"
          :disabled="loadingRequest"
          @click="showQuestion"
        />
      </transition>

      <transition name="fade">
        <div
          v-if="
            is_available(questionIndex + 1) &&
            recorderState !== 'recording' &&
            (recorderState !== 'uploading' || (currentQuestion && !!currentQuestion.video))
          "
          class="next-question"
        >
          <vbutton
            :loading="loadingRequest"
            :disabled="loadingRequest"
            :label="
              currentQuestion && !!currentQuestion.video
                ? 'videoInterview.checkVideo.nextQuestionAndContinue'
                : 'videoInterview.checkVideo.skip'
            "
            @click="change_index(questionIndex + 1)"
          />
        </div>
      </transition>

      <template v-if="show_video_recorder">
        <transition name="fade">
          <vbutton
            v-if="
              recorderState === 'ready' &&
              (has_video_question ? true : currentQuestion ? currentQuestion.is_shown : true)
            "
            label="videoInterview.checkVideo.startRecord"
            :loading="loadingRequest"
            :disabled="!!(has_video_question && has_preparation_time && !is_player_ended && !is_video_question_blocked)"
            @click="onStartRecording"
          />

          <vbutton
            v-else-if="recorderState === 'preparing'"
            :text="$tr('videoInterview.checkVideo.prepare') + '...'"
            disabled
          />

          <vbutton
            v-else-if="recorderState === 'recording'"
            class="video__button video__button_red"
            :label="
              !isVideoresume ? 'videoInterview.checkVideo.stopRecord' : 'vacancy.openRegistrationVideoresume.video.stop'
            "
            :disabled="loadingStoprecording || loadingRequest"
            @click="onStopRecording"
          />

          <vbutton
            v-else-if="recorderState === 'uploading'"
            :text="`${$tr('videoInterview.checkVideo.upload')}... ${uploadProgress}%`"
            disabled
          />
        </transition>
      </template>

      <transition name="fade">
        <div v-if="isSubmitEnabled()" class="pagination__button-container pagination__button-container--mobile">
          <vbutton
            label="videoInterview.checkVideo.nextQuestionAndFinish"
            :loading="loadingRequest"
            :disabled="loadingRequest"
            @click="saveVideointerview"
          />

          <span v-if="!isVideoresume" class="terms">
            {{ $tr('videoInterview.checkVideo.termsOfUse') }}
            <a :href="`https://vcv.${current_domain}/pages/privacy-policy`" target="_blank" rel="noopener">
              {{ $tr('videoInterview.checkVideo.termsLink') }}
            </a>
          </span>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import PROCESS_NAMES from '@/process/names';
import isMobile from 'ismobilejs';
import VueDraggableResizable from 'vue-draggable-resizable';
import { formattedPreparationTime } from '@/_helpers/utils.js';
import BrowserStore from 'store';
import SafariBrowserDetector from '@/components/mixin/SafariBrowserDetector.js';
import StreamVideoPlayer from './StreamVideoPlayer';

import QuestionPaginator from './Paginator.vue';
import QuestionPaginatorCarousel from './PaginatorCarousel.vue';

export default {
  name: 'InterviewVideointerviewVideointerview',

  components: {
    VueDraggableResizable,
    QuestionPaginator,
    QuestionPaginatorCarousel,
    StreamVideoPlayer
  },

  mixins: [SafariBrowserDetector],

  props: {
    interview: Object,
    isVideoresume: Boolean
  },

  data() {
    return {
      recorderAction: 'ready',
      retake: this.isVideoRecorded ? this.isVideoRecorded : false,
      switchToPlayer: true,
      preparationTimer: null,
      preparationTime: null,
      preparationTimeStamp: null,
      // flag determine if show question is processing right now (to prevent double click, which occur record bug)
      showQuestionProcessing: false,
      stage: 'start',
      is_player_visible: true,
      is_player_ended: false,
      is_player_started: false,
      is_play: false,
      is_vrecorder_visible: true,
      is_player_error: false,
      repeat_video_src: false,
      questions_timers: [],
      blocked_questions: [],
      isShowQuestionDuration: true,
      recorderState: '',
      width: 0,
      height: 0,
      x: 0,
      y: 0,
      resizableHeight: 0,
      recordplay: null,
      spinner: null,
      bandwidth: 1024 * 1024,
      myname: null,
      streamRecording: false,
      playing: false,
      recordingId: null,
      selectedRecording: null,
      selectedRecordingInfo: null,
      doSimulcast: '',
      videoId: null,
      recorderQuestions: [],
      requestedVideoSrc: false,
      isStopped: true,
      isStreamPlaying: false,
      isStreamPlayingEnd: false,
      currentStreamEvent: '',
      isMobile,
      isStartPlayer: false,
      isRecorderLoaded: true,
      loadingStoprecording: false,
      loadingShowQuestion: false,
      loadingNextQuestion: false,
      loadingSkipQuestion: false,
      loadingStream: false
    };
  },

  computed: {
    ...mapState('interview/videointerview', ['uploadProgress', 'loading', 'error', 'streamIds', 'recording']),

    ...mapGetters('process', ['getProcessName']),

    ...mapGetters('interview/videointerview', {
      videointerview: 'one',
      questions: 'questions',
      isVideoAnswerEnabled: 'isVideoAnswerEnabled',
      currentQuestion: 'currentQuestion'
    }),

    ...mapGetters('services', ['current_domain']),

    loadingRequest() {
      return (
        this.loading ||
        this.loadingStoprecording ||
        this.loadingShowQuestion ||
        this.loadingNextQuestion ||
        this.loadingSkipQuestion
      );
    },

    isStreamRecord() {
      return this.isIos && this.checkIosVersion < 14.3;
    },

    formattedPreparationTime() {
      return formattedPreparationTime(this.preparationTime);
    },

    isSkipped() {
      return this.is_available(this.questionIndex + 1) && !(this.currentQuestion && !!this.currentQuestion.video);
    },

    isSubmitEnabledProp() {
      const question = this.currentQuestion;

      // if this is last question and is not mandatory allow to submit
      return (
        this.questionIndex === this.questions.length - 1 &&
        ((question.video && (question.video.source || question.video.stream_recording_name)) ||
          (!this.isCurrentQuestionMandatory && this.isCurrentQuestionSkipped) ||
          this.currentQuestion.is_blocked)
      );
    },

    player_disabled() {
      return this.has_video_question && this.is_player_ended;
    },

    is_video_question_blocked() {
      return this.blocked_questions.find(question => {
        return question.id === this.currentQuestion.id && question.is_blocked;
      });
    },

    has_preparation_time() {
      if (!this.currentQuestion) {
        return false;
      }
      return this.currentQuestion.preparation_time !== null;
    },

    is_youtube() {
      return !!(
        this.currentQuestion.question_media.question_video_url &&
        this.currentQuestion.question_media.question_video_url.length
      );
    },

    is_show_player() {
      return (
        this.is_player_visible &&
        this.currentQuestion &&
        (this.isVideoRecorded || this.is_question_blocked) &&
        this.switchToPlayer
      );
    },

    isPaginatorDisabled() {
      return (
        this.recording ||
        !!this.preparationTime ||
        (this.currentQuestion &&
          this.currentQuestion.is_shown &&
          this.currentQuestion.is_required &&
          this.currentQuestion.video === null &&
          !this.currentQuestion.is_blocked)
      );
    },

    video_player_src() {
      if (
        !this.currentQuestion ||
        !this.currentQuestion.video ||
        (!this.currentQuestion.video.source && !this.isStreamRecord)
      ) {
        return '';
      }
      let src =
        this.currentQuestion && this.currentQuestion.video && this.currentQuestion.video.source
          ? this.noCache(this.currentQuestion.video.source)
          : '';

      if (this.isStreamRecord) {
        if (src.indexOf('.mp4') === -1) {
          src = '';
        }
        src = src || '';
      }
      return src;
    },

    is_question_blocked() {
      if (!this.currentQuestion) {
        return false;
      }

      return this.currentQuestion.is_blocked;
    },

    has_video_question() {
      if (!this.currentQuestion) {
        return false;
      }

      return (
        this.currentQuestion.question_media &&
        (this.currentQuestion.question_media.question_video || this.currentQuestion.question_media.question_video_url)
      );
    },

    question_video() {
      if (!this.currentQuestion || !this.currentQuestion.question_media) {
        return '';
      }

      return this.currentQuestion.question_media.question_video
        ? this.currentQuestion.question_media.question_video.source
        : this.currentQuestion.question_media.question_video_url;
    },

    isVideoRecorded() {
      let isRecorded = !!(
        this.currentQuestion &&
        this.currentQuestion.video &&
        (this.currentQuestion.video.source || this.currentQuestion.video.stream_recording_name)
      );
      if (this.isStreamRecord) {
        isRecorded = isRecorded || !!this.recorderQuestions.find(item => item === this.currentQuestion.id);
      }
      return isRecorded;
    },

    questions_paginator_list() {
      if (!this.questions || !this.questions.length) {
        return [];
      }

      return this.questions.map((item, index) => ({
        index,
        is_active: this.questionIndex === index,
        is_available: !this.isPaginatorDisabled && item.is_available,
        is_done: item.video,
        is_skipped: item.is_skipped,
        is_shown: item.is_shown,
        disabled:
          item.disabled || !item.is_available || (item.is_skipped && item.is_shown && item.preparation_time !== null)
      }));
    },

    is_videointerview_started() {
      if (!this.questions || !this.questions.length) {
        return false;
      }

      return this.questions.find(item => (item ? item.is_shown : false));
    },

    is_videointerview_started_index() {
      let questions_array = [];
      if (this.is_videointerview_started) {
        questions_array = this.questions;
      }

      let index = 0;

      questions_array.forEach((item, i) => {
        if (item.is_shown || item.is_skipped) {
          index = i;
        }
      });

      return index;
    },

    is_skip_visible() {
      return !this.isCurrentQuestionMandatory && !this.isVideoRecorded && !this.isCurrentQuestionSkipped;
    },

    is_skip_disable() {
      return this.stage === 'recording' || this.uploadProgress;
    },

    tooltip_content() {
      return this.$tr('videoInterview.skipVideo.helper');
    },

    isRerecordAllowed() {
      return this.currentQuestion ? this.currentQuestion.retryable : false;
    },

    // if there is no is_required property in question object, by default question is acceptable as required
    isCurrentQuestionMandatory() {
      return this.currentQuestion
        ? !this.currentQuestion.hasOwnProperty('is_required') || this.currentQuestion.is_required
        : false;
    },

    // determine if question is already benn skipped
    isCurrentQuestionSkipped() {
      return this.currentQuestion ? this.currentQuestion.is_skipped : false;
    },

    questionIndex: {
      get() {
        return this.$store.getters['interview/videointerview/questionIndex'];
      },

      set(val) {
        const correctVal = Math.min(val, this.questions.length - 1);
        this.clearPreparation();

        this.$store
          .dispatch('interview/videointerview/setCurrentQuestion', {
            interview: this.interview,
            questionIndex: correctVal
          })
          .then(() => {
            this.fetch();
          });
        this.showQuestionProcessing = false;

        this.is_player_visible = false;
        setTimeout(() => {
          this.is_player_visible = true;
        }, 100);
        this.is_vrecorder_visible = false;
        setTimeout(() => {
          this.is_vrecorder_visible = true;
        }, 100);
        this.is_player_ended = false;
        this.is_player_started = false;
        this.is_play = false;
      }
    },

    duration() {
      return this.currentQuestion ? this.currentQuestion.answer_time : 0;
    },

    noAnswerTime() {
      return this.currentQuestion && this.currentQuestion.answer_time === 0;
    },

    questionDuration() {
      if (
        !this.currentQuestion ||
        !this.currentQuestion.question_media ||
        !this.currentQuestion.question_media.question_video
      ) {
        return 0;
      }
      return this.currentQuestion.question_media.question_video.duration || 0;
    },

    isQuestionHidden() {
      return this.currentQuestion ? !this.currentQuestion.is_shown : false;
    },

    is_videointerview_start() {
      return (
        this.is_videointerview_started_index !== null &&
        this.is_videointerview_started_index >= 0 &&
        !this.isCurrentQuestionMandatory
      );
    },

    show_video_recorder() {
      return (
        (!this.is_question_blocked || this.isRerecordAllowed) &&
        (!this.isVideoRecorded || (!this.switchToPlayer && this.isRerecordAllowed))
      );
    }
  },

  watch: {
    interview: {
      immediate: true,
      handler() {
        this.fetch();
      }
    },

    is_player_error: {
      immediate: true,
      handler(val) {
        if (val && !this.repeat_video_src) {
          this.repeat_video_src = true;
          this.fetch();
        }
      }
    },

    stage: {
      immediate: true,
      handler(val) {
        if (val === 'preparation_time' || val === 'recording') {
          window.onbeforeunload = function (e) {
            return e;
          };
        } else {
          window.onbeforeunload = null;
        }
      }
    },

    isSubmitEnabledProp: {
      immediate: true,
      handler(val) {
        this.$store.commit('interview/videointerview/setSubmitEnabledProp', val);
      }
    },

    currentQuestion(val, oldVal) {
      if (val && oldVal && oldVal.id && val.id === oldVal.id) {
        return;
      }

      if (
        val &&
        val.question_media &&
        !val.question_media.is_question_media_seen &&
        !val.question_media.source &&
        !val.question_media.video_url
      ) {
        this.showVideo();
        return;
      }
      if (val && val.preparation_time !== null && val.is_shown) {
        this.showQuestion();
      } else if (val && val.retryable && !val.is_shown) {
        this.showQuestion();
      } else if (val && !val.preparation_time) {
        this.showQuestion();
      }
      this.repeat_video_src = false;
      this.is_player_error = false;
      this.retake = true;

      // if (this.isIos) {
      //   this.$store.commit('interview/videointerview/setStreamId', +`${val.id}${Math.floor(Math.random() * 10000000)}`);
      // }
    },

    /* videoInterviewData: {
      handler(val) {
        if (this.is_videointerview_start) {
          this.questionIndex = this.is_videointerview_started_index;
        } else if (this.is_videointerview_started_index === -1) {
          this.questionIndex = 0;
        }
      }
    }, */

    isVideoRecorded(val) {
      this.retake = !!val;
      this.switchToPlayer = true;
    },

    video_player_src(val) {
      this.isRecorderLoaded = false;
      setTimeout(() => {
        this.$nextTick().then(() => {
          this.isRecorderLoaded = true;

          if (val && this.isStartPlayer) {
            this.$nextTick().then(() => {
              this.$refs.recorder.$refs.video_player.$refs.videoPlayer.player.play();
            });
          }
        });
      }, 300);
    }
  },

  created() {
    if (this.is_videointerview_started_index === -1) {
      this.questionIndex = 0;
    } else {
      this.questionIndex = this.is_videointerview_started_index;
    }

    if (this.questions) {
      this.questions_timers = this.questions.map(question => {
        return {
          [question.id]: BrowserStore.get(`videointerview_${question.id}_timer`) || 0
        };
      });

      this.blocked_questions = this.questions.map(question => {
        if (question.question_media && question.question_media.is_question_media_seen && question.preparation_time) {
          let local_storage_time = null;

          this.questions_timers.forEach(question => {
            for (var key in question) {
              if (key === this.currentQuestion.id) {
                local_storage_time = question[key];
              }
            }
          });

          if (!local_storage_time) {
            return {
              id: question.id,
              is_blocked: false
            };
          }

          if (local_storage_time > 30 || local_storage_time === -1) {
            return {
              id: question.id,
              is_blocked: true
            };
          }

          return {
            id: question.id,
            is_blocked: false
          };
        }

        return {
          id: question.id,
          is_blocked: false
        };
      });
    }

    // PROCESS QMARKETING
    if (this.getProcessName !== undefined) {
      if (this.getProcessName === PROCESS_NAMES.QMARKETING) {
        // Facebook Pixel Code
        fbq('init', '867651840238457');
        // End Facebook Pixel Code
        gtag('config', 'UA-147625550-1', { groups: 'qmarketing' });
      }
    }
  },

  mounted() {
    this.resizableHeight = document.documentElement.clientHeight - 110 - 77;
  },

  beforeMount() {
    this.$store.commit('interview/videointerview/setRecording', false);
    this.set_current_domain();
  },

  beforeDestroy() {
    this.$store.commit('services/clear');
  },

  methods: {
    ...mapMutations('services', ['set_current_domain']),

    onStartRecording() {
      this.$refs.recorder.$refs.video_recorder.onStartRecording();
    },

    onStopRecording() {
      this.$refs.recorder.$refs.video_recorder.onStopRecording();
    },

    onResize(x, y, width, height) {
      this.x = x;
      this.y = y;
      this.width = width;
      this.height = height;
    },

    setRecorderState(v) {
      this.recorderState = v;
      this.$emit('setRecordingState', v);
    },

    retakeTestVideo() {
      this.switchToPlayer = false;
      this.$store.commit('interview/videointerview/setUploadProgress', 0);
    },

    player_error() {
      this.is_player_error = true;
    },

    fetch() {
      this.interview.token && this.$store.dispatch('interview/videointerview/fetch', { interview: this.interview });
    },

    isSubmitEnabled() {
      const question = this.currentQuestion;

      // if this is last question and is not mandatory allow to submit
      return (
        this.questionIndex === this.questions.length - 1 &&
        ((question.video && (question.video.source || question.video.stream_recording_name)) ||
          (!this.isCurrentQuestionMandatory && this.isCurrentQuestionSkipped) ||
          this.currentQuestion.is_blocked)
      );
    },

    showVideo() {
      this.$store.dispatch('interview/videointerview/showVideo', { interview: this.interview }).then(() => {
        this.fetch();
      });
    },

    showQuestion() {
      if (this.loadingShowQuestion) {
        return;
      }
      // deprecated processing same code on multiclick
      if (this.showQuestionProcessing) {
        return;
      }

      if (this.is_player_started) {
        this.initPreparation(this.preparationTime);
        return;
      }

      this.loadingShowQuestion = true;
      this.showQuestionProcessing = true;
      this.clearPreparation();

      this.$store
        .dispatch('interview/videointerview/showQuestion', { interview: this.interview })
        .then(timeout => {
          if (timeout > 0 && !this.currentQuestion.is_blocked && !this.isVideoRecorded) {
            this.initPreparation(timeout);
            this.is_player_started = true;
          }
          this.loadingShowQuestion = false;
          this.fetch();
        })
        .catch(err => {
          let error = err;

          if (err === 'Network error') {
            error = this.$tr('common.error.networkError');
          }

          this.$store.dispatch('global/setError', error);
          this.showQuestionProcessing = false;
          this.loadingShowQuestion = false;
        });
    },

    // send request and increment index
    skipQuestion() {
      if (this.loadingSkipQuestion || this.loadingRequest) {
        return;
      }
      this.loadingSkipQuestion = true;
      this.$store
        .dispatch('interview/videointerview/skipQuestion', { interview: this.interview })
        .then(() => {
          this.stage = '';

          if (!this.error) {
            this.questionIndex++;
            this.paginationIndexChange(this.questionIndex);
          }

          this.loadingSkipQuestion = false;
        })
        .then(() => {
          this.fetch();
        })
        .catch(() => {
          this.loadingSkipQuestion = false;
        });
    },

    is_available(index) {
      return !!(this.questions_paginator_list[index] && this.questions_paginator_list[index].is_available);
    },

    // process some actions on pagination event indexChange
    paginationIndexChange() {
      // set flag to false to enable behaviour of showQuetion button
      this.showQuestionProcessing = false;
      this.stage = '';
      // this.stopStreamRecording();
      this.loadingNextQuestion = false;
    },

    change_index(index) {
      this.loadingNextQuestion = true;
      this.questionIndex = index;
      this.paginationIndexChange(index);
    },

    startRecording() {
      this.loadingStoprecording = false;
      this.clearPreparation();
      this.is_player_ended = true;

      if (this.noAnswerTime && this.has_video_question) {
        this.skipQuestion();
        return;
      }

      if (
        this.$refs.video_question &&
        this.$refs.video_question.$refs.video_player &&
        this.$refs.video_question.$refs.video_player.youtube_videojs &&
        this.$refs.video_question.$refs.video_player.youtube_videojs.pause
      ) {
        this.$refs.video_question.$refs.video_player.youtube_videojs.pause();
      }

      if (
        this.$refs.video_question &&
        this.$refs.video_question.$refs.video_player &&
        this.$refs.video_question.$refs.video_player.$refs.videoPlayer &&
        this.$refs.video_question.$refs.video_player.$refs.videoPlayer.player &&
        this.$refs.video_question.$refs.video_player.$refs.videoPlayer.player.pause
      ) {
        this.$refs.video_question.$refs.video_player.$refs.videoPlayer.player.pause();
      }

      if (this.recorderAction !== 'ready') {
        throw Error('Record not ready');
      }

      if (!this.isVideoRecorded || this.isRerecordAllowed) {
        if (!!this.has_video_question && !this.currentQuestion.is_shown) {
          this.$store
            .dispatch('interview/videointerview/showQuestion', { interview: this.interview })
            .then(() => {
              this.recorderAction = 'start';
              this.stage = 'recording';
              return this.$store
                .dispatch('interview/videointerview/startRecording', {
                  interview: this.interview
                })
                .then(data => {
                  return data;
                });
            })
            .catch(err => {
              let error = err;
              if (err === 'Network error') {
                error = this.$tr('common.error.networkError');
              }
              this.$store.dispatch('global/setError', error);
              this.showQuestionProcessing = false;
            });
        } else {
          this.$store.dispatch('interview/videointerview/startRecording', {
            interview: this.interview
          });
          this.recorderAction = 'start';
          this.stage = 'recording';
        }
      }
    },

    stopRecording() {
      if (this.recorderAction !== 'start') {
        throw Error('Record not started');
      }

      this.loadingStoprecording = true;
      this.recorderAction = 'stop';
      this.stage = '';

      setTimeout(() => {
        this.recorderAction = 'ready';
      }, 800);
    },

    async completeRecording(model) {
      // if (!this.isStreamRecord) {
      this.recorderAction = 'ready';
      this.stage = '';

      await this.$store
        .dispatch('interview/videointerview/stopRecording', {
          interview: this.interview,
          blob: model.blob
        })
        .then(() => {
          this.loadingStoprecording = false;
          this.switchToPlayer = true;
        })
        .then(() => {
          this.fetch();
        })
        .catch(err => {
          let error = err;
          if (err === 'Network error') {
            error = this.$tr('common.error.networkError');
          }
          this.$store.dispatch('global/setError', error);
        });
    },

    saveVideointerview() {
      this.$store
        .dispatch('interview/videointerview/end', {
          interview: this.interview
        })
        .then(() => {
          if (this.isVideoresume) {
            this.$store.dispatch('record_interview/loadResponse', this.interview.token);
          }

          // PROCESS QMARKETING (Facebook Pixel event)
          if (this.getProcessName !== undefined) {
            if (this.getProcessName === PROCESS_NAMES.QMARKETING) {
              // fbq('track', 'LeadFinish');
              fbq('trackSingleCustom', '867651840238457', 'LeadFinish');

              gtag('event', 'Finish', {
                event_category: 'Video',
                event_label: 'Sales Manager',
                send_to: 'qmarketing'
              });
            }
          }
        })
        .catch(error => {
          this.loadingStoprecording = false;
          this.switchToPlayer = true;
          this.$store.dispatch('global/setError', error);
          this.setRecorderState('');
          this.$noty.error(this.$tr('videoInterview.checkVideo.errorSend'));
        })
        .then(() => {
          this.fetch();
        })
        .then(() => {
          if (this.isVideoresume) {
            this.$router.push({
              name: 'interview-videoresume-feedback',
              params: { token: this.interview.token }
            });
            return;
          }
          // this.$router.push({name: 'interview', params: {token: this.interview.token}});
          this.$store.dispatch('record_interview/load', this.interview.token);
          this.stage = '';
          // document.location.reload(true);
        })
        .catch(() => {
          this.$noty.error(this.$tr('videoInterview.checkVideo.errorSend'));
        });
    },

    noCache(str) {
      return str + '?' + Math.random();
    },

    initPreparation(timeout) {
      this.preparationTime = timeout;
      this.preparationTimeStamp = new Date().getTime();
      this.stage = 'preparation_time';

      this.preparationTimer = setInterval(() => {
        const current_time = new Date();
        const diff_timestamp = (current_time.getTime() - this.preparationTimeStamp) / 1000;
        this.preparationTime = timeout - Math.floor(diff_timestamp);

        if (this.preparationTime <= 0 && this.preparationTime >= -30) {
          this.clearPreparation();
          this.startRecording();
        } else if (this.preparationTime < -30) {
          this.clearPreparation();
          this.fetch();
        }
      }, 1000);
    },

    pausePreparation() {
      clearInterval(this.preparationTimer);
      this.stage = 'start';
    },

    clearPreparation() {
      if (this.preparationTimer) {
        clearInterval(this.preparationTimer);
      }

      this.preparationTimer = null;
      this.preparationTime = null;
      this.stage = '';
      window.localStorage.removeItem('timestamp');
      this.preparationTimeStamp = null;
    },

    player_play() {
      this.isShowQuestionDuration = false;

      if (this.isVideoRecorded) {
        return;
      }
      this.is_play = true;

      if (this.is_player_started) {
        this.pausePreparation();
      }
    },

    recorder_play() {
      if (this.isStreamRecord && this.video_player_src === '' && !this.requestedVideoSrc) {
        this.requestedVideoSrc = true;

        this.fetch().then(() => {
          if (this.video_player_src === '') {
            setTimeout(() => {
              this.fetch();
            }, 7000);
          }
        });
      }
    },

    player_pause() {
      if (this.isVideoRecorded || this.stage === 'recording' || this.is_player_ended) {
        return;
      }

      if (this.is_player_started) {
        this.initPreparation(this.preparationTime);
      } else {
        this.showQuestion();
      }
    },

    player_end() {
      if (this.isVideoRecorded) {
        return;
      }

      BrowserStore.set(`videointerview_${this.currentQuestion.id}_timer`, -1);
      this.is_player_ended = true;
      this.showQuestion();
    },

    onPlayerTimeupdate(val) {
      if (!this.currentQuestion.preparation_time) {
        return;
      }

      let local_storage_time = null;

      this.questions_timers.forEach(question => {
        for (var key in question) {
          if (key === this.currentQuestion.id) {
            local_storage_time = question[key];
          }
        }
      });

      const time = local_storage_time + val;

      BrowserStore.set(`videointerview_${this.currentQuestion.id}_timer`, time);
    },

    startPlayout() {
      if (this.playing) {
        return;
      }

      // const playoutId = 51070442866430760;
      // Start a playout
      this.streamRecording = false;
      this.playing = true;
      const streamId = this.streamIds ? this.streamIds[this.currentQuestion.id] : null;

      if (streamId === undefined || streamId === null) {
        this.playing = false;
        return;
      }
      const play = { request: 'play', id: parseInt(streamId) };
      this.recordplay.send({ message: play });
    },

    startPlayer() {
      this.isStartPlayer = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.vid-int-videoTest__new-video {
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $green;
  color: #fff;
  font-size: 18px;
  user-select: none;
  cursor: default;
}

.vid-int-videoInterview__button {
  margin-top: 20px;
  user-select: none;
}

.pagination__button-container {
  display: flex;
  flex-direction: column;
  min-width: 30%;
  align-items: center;

  a {
    color: $green;
  }
}

.pagination__button-container--mobile {
  margin-bottom: 8px;

  .terms {
    max-width: 365px;
    text-align: center;

    @media only screen and (max-width: 480px) {
      width: 100%;
      max-width: 100%;
    }
  }
}

.pagination__button-container-flex-row {
  display: flex;
  min-width: 30%;
  margin-top: 20px;
  justify-content: flex-start;
}

.videoInterview__question {
  position: relative;
  margin: 0;
  vertical-align: middle;
  box-sizing: border-box;
  white-space: pre-wrap;
  font-size: 15px;
  line-height: 24px;
  display: block;
  unicode-bidi: embed;

  ::v-deep {
    img {
      max-width: 100%;
    }
    a {
      color: $green;
      text-decoration: underline;
      transition: color 0.2s;

      &:hover {
        color: $green-hover;
        transition: color 0.2s;
      }
    }
    ul,
    ol {
      padding-left: 30px;
      margin: 0;
    }
  }
}

.videoInterview__preparation {
  display: flex;
  align-items: center;
  font-size: 15px;
  line-height: 24px;
  color: #9faab6;
}

.videoInterview__questions-container {
  position: relative;
  margin-right: 1.5rem;

  @media only screen and (max-width: 1024px) {
    margin-right: 0;
    margin-top: 1.5rem;
  }

  @media only screen and (max-width: 480px) {
    margin-top: 1rem;
  }
}

.videoInterview__container {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;

  @media only screen and (max-width: 1024px) {
    flex-direction: column-reverse;
    flex: 1;
    justify-content: flex-end;
  }
}

.videoInterview__questions {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  font-size: 1rem;
  line-height: 1.5rem;
  min-height: 37.5rem;

  @media only screen and (max-width: 1024px) {
    height: 100%;
    flex: 1;
    min-height: auto;
  }
}

.videoInterview__questionCounter {
  position: absolute;
  left: 0;
  bottom: calc(100% + 0.5rem);
  font-size: 15px;
  line-height: 24px;
  color: #9faab6;
  display: block;
  width: 100%;
  max-width: 27.75rem; // 444px
  margin-right: auto;

  @media only screen and (max-width: 1024px) {
    display: none;
  }
}

.videoInterview__questionCounter--mobile {
  display: none;

  @media only screen and (max-width: 1024px) {
    position: static;
    display: block;
    max-width: 35rem; // 560px
    width: 100%;
    margin-top: 0.75rem;
    margin-bottom: 0.5rem;
  }

  @media only screen and (max-width: 480px) {
    display: flex;
    flex-wrap: nowrap;
  }
}

.question-info-left {
  display: inline;
  @media only screen and (max-width: 480px) {
    flex: 1;
    display: flex;
    flex-wrap: nowrap;

    span {
      white-space: nowrap;
    }
  }
}

.question-info-right {
  display: inline;

  @media only screen and (max-width: 480px) {
    display: block;
  }
}

.video-descr {
  display: block;
  // font-family: Roboto;
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 22px;
  color: #9faab6;
}

.skip-block {
  font-size: 15px;

  @media only screen and (max-width: 480px) {
    width: 100%;
  }

  &:before {
    content: '';
    display: table;
    clear: both;
  }

  .button_custom {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 20px;
    background-color: #f4f5f7;
    color: #9faab6;
    font-size: 15px;
    // font-family: Roboto;
    line-height: 20px;
    cursor: pointer;
    border-radius: 2px;
    text-align: center;
    box-sizing: border-box;
    max-height: 40px;
    min-height: 40px;
    height: 40px;
    border: 0;
    text-transform: none;

    @media only screen and (max-width: 1024px) {
      width: 365px;
      height: 50px;
      max-height: 50px;
      min-height: 50px;
    }

    @media only screen and (max-width: 480px) {
      width: 100%;
    }

    &.disabled {
      background-color: #f4f5f7;
      opacity: 0.7;
    }
    &:hover:not(.disabled) {
      background-color: #e3e6ed;
    }
    &:active:not(.disabled) {
      background-color: #d2d6e0;
    }

    .skip-block__tooltip {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
    }
  }
}

.videoInterview__questions__video {
  .button_custom {
    margin-top: 0;
  }
}

.footer-section-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;

  & > div,
  & > button {
    margin-left: 0.5rem;
  }

  @media only screen and (max-width: 1024px) {
    display: none;
  }

  ::v-deep .vbutton.grey {
    background: #f4f5f7 !important;
    color: #9faab6 !important;
  }
}

.recorder-section {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;

  &.recorder-section-record {
    @media only screen and (max-width: 480px) {
      flex: 1;
    }
  }
}

.vid-init-videoTest__rerecord-button {
  position: static;
  margin-top: 0;
  width: 160px;
  margin-left: auto;
}

.videoInterview__questions-container,
.vid-int-videoTest__video-container {
  width: 444px;
  min-width: 444px;
  box-sizing: border-box;
  overflow: hidden;

  @media only screen and (max-width: 1024px) {
    max-width: 35rem; // 560px
    width: 100% !important;
    min-width: auto;
    height: auto !important;
  }

  @media only screen and (max-width: 480px) {
    max-width: 100%; // 560px
    width: 100% !important;
    min-width: auto;
    height: auto !important;
  }
}

.videoInterview__questions__video {
  .skip-block {
    .button_custom {
      border: none;
    }
  }
}

.questions-actions {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: -80px;

  .vid-init-videoTest__rerecord-button {
    margin-right: 20px;
    margin-top: 0;
    width: 180px;
  }
}

.video-question-wrap {
  position: relative;

  @media (min-width: 1025px) {
    // overflow-y: auto;
    // max-height: 352px;
  }
}

.question-info-block {
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;
  // font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 15px;
  line-height: 22px;
  color: #233346;
}

.notification-message {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: -17px;
  margin-bottom: 13px;
  margin-left: auto;
  // font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 15px;
  line-height: 22px;
  color: #9faab6;
}

.video-container--content {
  width: 100%;
  max-width: 912px;
  display: flex;
  align-items: center;
  flex: 1;

  @media only screen and (max-width: 1024px) {
    margin-bottom: 0;
    align-items: flex-start;
    flex-direction: column;
    height: 100%;
  }

  @media only screen and (max-width: 480px) {
    max-width: 100vw;
  }

  ::v-deep {
    .vid-int-videoTest__video-container {
      @media only screen and (max-width: 1024px) {
        margin-bottom: 0;
      }
    }
    .foo {
      display: none;
      padding: 1rem;
      width: 100%;
      min-width: 100%;
      min-height: 88px;
      height: 88px;
      z-index: 9999 !important;
      background: linear-gradient(360deg, #ffffff 2.5%, rgba(255, 255, 255, 0.8) 100%);
      position: absolute;
      bottom: 0;
      // bottom: 167px;
      top: auto !important;
      left: 0;
      overflow: hidden;
      box-sizing: border-box;
      border: 0;
      max-height: calc(100vh - 11.75rem);
      max-height: calc(var(--vh, 1vh) * 100 - 11.75rem);

      @media only screen and (max-width: 480px) {
        &.foo-video {
          display: flex;
          flex-direction: column;
          transform: translate(0px, 0px) !important;
        }
      }
    }

    .foo-content {
      overflow-y: auto;

      ::v-deep {
        img {
          max-width: 100%;
        }
        a {
          color: $green;
          text-decoration: underline;
          transition: color 0.2s;
          &:hover {
            color: $green-hover;
            transition: color 0.2s;
          }
        }
        ul,
        ol {
          padding-left: 30px;
          margin: 0;
        }
      }
    }

    .foo-content-wrapper {
      flex: 1;
      display: flex;
      position: relative;
      flex-direction: column;
      max-height: 100%;
    }

    .handle-tm,
    .handle-bm {
      display: block !important;
    }

    .handle-tm {
      top: 4px;
      border: 0;
      background: #ffffff;
      border-radius: 20px;
      cursor: grab;
      width: 80px;
      height: 4px;
      left: calc(50% - 40px);
    }
  }

  ::v-deep .video-js {
    @media only screen and (max-width: 1024px) {
      width: 100% !important;
      height: auto !important;
      padding-bottom: 66.6% !important;
    }
  }

  ::v-deep {
    .state-recording {
      .video-js {
        @media only screen and (max-width: 480px) {
          width: 100% !important;
          height: 100% !important;
          padding-bottom: 0 !important;
          display: flex;
          flex-direction: column;
          flex: 1;
        }
      }

      .mask-on-video {
        @media only screen and (max-width: 480px) {
          display: none;
        }
      }

      .vid-int-videoTest__video-container {
        @media only screen and (max-width: 480px) {
          display: flex;
          flex-direction: column;
          flex: 1;

          & > div {
            display: flex;
            flex-direction: column;
            flex: 1;
            & > div {
              display: flex;
              flex-direction: column;
              flex: 1;
            }
          }
          .video__container,
          .video-player {
            display: flex;
            flex-direction: column;
            flex: 1;
          }
        }
      }
    }
  }
}

.fixed-block-interview {
  display: none;
  position: sticky;
  bottom: 40px;
  left: 0;
  background: linear-gradient(360deg, #ffffff 70px, rgba(255, 255, 255, 0.5) 100%);
  padding: 1.875rem 1rem;
  z-index: 99999;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  box-sizing: border-box;

  @media only screen and (max-width: 1024px) {
    display: flex;
    padding: 2.5rem 1rem 1.25rem;
  }

  @media only screen and (max-width: 480px) {
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 0.75rem;
    background: linear-gradient(360deg, #ffffff 62px, rgba(255, 255, 255, 0.5) 100%);
  }

  &.fixed-block-interview--recording {
    padding-top: 0;
    @media only screen and (max-width: 1024px) {
      padding-top: 0;
    }
    @media only screen and (max-width: 480px) {
      padding-top: 0;
      position: static;
    }
  }

  ::v-deep {
    .button-grey.vbutton {
      background: #f4f5f7 !important;
      color: #9faab6 !important;
    }
  }

  ::v-deep {
    .vbutton {
      width: 365px;
      height: 50px;
      max-height: 50px;
      min-height: 50px;

      @media only screen and (max-width: 480px) {
        width: 100%;
      }

      &.video__button_red {
        background: #fafafa !important;
        color: #b36565 !important;

        &:hover,
        &:focus,
        &:active {
          background: #fafafa !important;
          color: #b36565 !important;
        }
      }
    }

    > * {
      &:not(:first-child) {
        margin-top: 0.5rem;
      }
    }

    div {
      @media only screen and (max-width: 480px) {
        width: 100%;
      }
    }
  }
}

.buttons-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.paginator {
  margin-bottom: 6.25rem;
  z-index: 2;
}

.paginator--desktop {
  display: flex;

  @media only screen and (max-width: 1024px) {
    display: none;
  }
}

.paginator--mobile {
  display: none;
  justify-content: flex-start;
  margin-top: 0;
  margin-bottom: 0;
  box-sizing: border-box;

  @media only screen and (max-width: 1024px) {
    display: flex;
    max-width: calc(100vw - 2rem);

    ::v-deep {
      .swiper-container {
        margin-left: -1rem;
        margin-right: -1rem;
        width: calc(100% + 2rem);
        padding-left: 1rem;
      }
    }
  }
}

.videoInterview__preparation-text {
  display: block;
}
.videoInterview__preparation-video {
  display: none;
}

.videoInterview__questions__video {
  .videoInterview__preparation-text {
    display: none;
  }
  .videoInterview__preparation-video {
    display: block;
  }
}

.state-recording {
  @media only screen and (max-width: 480px) {
    .paginator,
    .videoInterview__questionCounter,
    .videoInterview__questions-container,
    .footer-section-wrapper,
    .pagination__button-container--desktop {
      display: none;
    }
  }
}

.mobile-question-descr {
  position: relative;
  width: 100%;
  height: 80px;
}

.terms {
  font-size: 15px;
  line-height: 24px;
}

.mobile-hidden {
  @media screen and (max-width: 1024px) {
    display: none;
  }
}

.loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  box-sizing: border-box;
  background-color: rgba(255, 255, 255, 0.5) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  .loading-spinner {
    display: block;
  }
}

.video-empty-block {
  height: 296px;
  width: 100%;

  @media screen and (max-width: 1024px) {
    display: none;
  }
}

.video-container--videoresume {
  ::v-deep {
    .paginator {
      .pagination-item {
        position: relative;
        background: #f4f5f7;
        border-radius: 4px;
        // font-family: 'Euclid Circular A';
        font-style: normal;
        font-weight: 500;
        font-size: 15px;
        line-height: 24px;
        color: #8d8d8d;
        margin-right: 4px;
        min-width: 40px;
        height: 32px;
        padding: 4px 17px;

        &:last-child {
          margin-right: 0;
        }

        &.is_active {
          color: #555555;

          &:before {
            content: '';
            background: $green;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            display: inline-block;
            margin-right: 3px;
          }
        }
      }
    }

    .videoInterview__questionCounter {
      // font-family: 'Euclid Circular A';
      font-style: normal;
      font-weight: normal;
      font-size: 15px;
      line-height: 24px;
      color: #c4b9d3;
      text-transform: lowercase;
    }

    .videoInterview__question {
      // font-family: 'Euclid Circular A';
      font-style: normal;
      font-weight: normal;
      font-size: 15px;
      line-height: 24px;
      color: #8d8d8d;
    }

    .videoInterview__preparation {
      // font-family: 'Euclid Circular A';
      font-style: normal;
      font-weight: normal;
      font-size: 15px;
      line-height: 24px;
      color: #c4b9d3;

      .videoInterview__preparation_counter {
        margin-left: 2px;
        // font-family: 'Euclid Circular A';
        font-style: normal;
        font-weight: 500;
        font-size: 15px;
        line-height: 24px;
        background: linear-gradient(283.83deg, #848bd3 0%, #bc89e5 100%);
        -webkit-background-clip: text !important;
        -webkit-text-fill-color: transparent !important;
      }
    }

    .skip-block {
      .button_custom {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 16px 36px;
        background-color: #f4f5f7;
        color: #9faab6;
        font-size: 15px;
        // font-family: 'Euclid Circular A';
        line-height: 24px;
        cursor: pointer;
        border-radius: 4px;
        max-height: 56px;
        min-height: 56px;
        height: 56px;
        text-transform: lowercase;
      }
    }
  }
}

.recorder-only-audio {
  ::v-deep {
    .stream-player.is-black,
    .stream-player-video {
      background-image: url('/static/images/elements/equalizer.jpg');
      background-repeat: no-repeat;
      background-position: top center;
      background-size: cover;
    }
  }
}

.videointerview-error-section {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
}

.videointerview-error-section_title {
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  color: #233346;
  margin-bottom: 106px;
}

.videointerview-error-section_img {
  margin-bottom: 2px;
  max-width: 124px;
  height: auto;
}

.videointerview-error-section_text {
  font-style: normal;
  font-weight: normal;
  font-size: 15px;
  line-height: 24px;
  text-align: center;
  color: #233346;
}
</style>
